/**
 * What she owns, as facts a page can draw (MAR-236).
 *
 * The website's half of the app's Membership screen. Everything here is pure:
 * it takes an entitlements payload, a profile and a clock, and returns rows and
 * phrases. Nothing in it fetches, and nothing in it decides what may be sold.
 *
 * ## The rule the app taught, kept verbatim
 *
 * NOTHING HERE MAY GUESS. A stage is included because the payload says so, a
 * date is printed because a readable one arrived, and a receipt link exists
 * because the Worker sent a URL. The failure this avoids is the one that
 * matters most on this surface: telling a paying customer she is on the free
 * plan because a read stumbled. That state is the page's error state, and it
 * is drawn from `isError` rather than from an empty object.
 *
 * ## Why the source line differs from the app's
 *
 * The app knows one shop. The website knows two, and a customer can easily
 * have bought her first trimester on her phone and the rest at a laptop. So
 * every purchased row says WHERE, and only a purchase made on the web can
 * carry a receipt, because Apple's receipts live in the App Store and not
 * with us.
 *
 * ## Two fields, two questions, and nothing reads the shop name for meaning
 *
 * Whether a row says "Purchased on 9 March" or "Given by Maria" is decided by
 * `kind`, and where it was bought is decided by `origin`. There is no shop
 * name to read: the legacy `source` field left the wire in MAR-236. That split
 * is the point: "did she pay for this" is not a fact
 * about which shop took the money, and answering it from a shop name means
 * every new shop and every new way of granting a stage has to be added to a
 * condition in the UI, where the cost of getting it wrong is telling a
 * customer she was given something she paid for.
 */

import type {
  MumHubEntitlements,
  MumHubStageEntitlement,
} from "./api";
import { deriveMumHubStage } from "./stage";
import {
  MUM_HUB_STAGES,
  isMumHubProductKey,
  stageDescriptor,
  type MumHubProductKey,
  type MumHubStageDescriptor,
  type MumHubStageId,
} from "./stages";

/** Month names, spelled out, as a British reader writes a date. */
export const MONTH_NAMES: readonly string[] = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/**
 * An ISO timestamp or date, as "3 March 2027". Null when it is not readable.
 *
 * Null rather than a fallback string, because every caller here draws NOTHING
 * for a date it cannot read. "Purchased on Invalid Date" is worse than a row
 * with no subtitle.
 */
export function formatMembershipDate(iso: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  // Round-tripped through UTC so `2027-02-30` is rejected rather than rolling
  // into March and printing a day she never saw.
  const utc = new Date(Date.UTC(year, month - 1, day));
  if (
    utc.getUTCFullYear() !== year ||
    utc.getUTCMonth() !== month - 1 ||
    utc.getUTCDate() !== day
  ) {
    return null;
  }
  return `${day} ${MONTH_NAMES[month - 1]} ${year}`;
}

/**
 * A due date at the precision she gave it.
 *
 * Month precision is stored as the 1st of that month, and printing
 * "1 March 2027" back to somebody who answered "March" invents a day she did
 * not pick.
 */
export function formatDueDate(
  iso: string,
  precision: "day" | "month" | null,
): string | null {
  const full = formatMembershipDate(iso);
  if (full === null) return null;
  if (precision !== "month") return full;
  const parts = full.split(" ");
  return `${parts[1]} ${parts[2]}`;
}

/** Lowercases a label for mid-sentence use. "New mum" is already there. */
export function midSentence(label: string): string {
  return label.charAt(0).toLowerCase() + label.slice(1);
}

/** "a", "a and b", "a, b and c". Oxford comma deliberately absent. */
function joinWords(words: readonly string[]): string {
  if (words.length === 0) return "";
  if (words.length === 1) return words[0];
  return `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
}

/** The trimester ordinal for a stage, or null for the one that has none. */
const TRIMESTER_ORDINAL: Record<MumHubStageId, string | null> = {
  "first-trimester": "First",
  "second-trimester": "Second",
  "third-trimester": "Third",
  "new-mum": null,
};

/**
 * What she owns, as a phrase. Empty string for nothing, so a caller has to
 * decide what "nothing" says rather than being handed a sentence for it.
 *
 * Stages arrive in whatever order they were found and leave in the order a
 * pregnancy runs, because "third and first trimester" is a phrase about
 * nothing. Consecutive trimesters share their noun: a naive join gives
 * "First trimester and second trimester", which nobody says out loud.
 */
export function planSummary(owned: readonly MumHubStageId[]): string {
  const inOrder = MUM_HUB_STAGES.filter((stage) => owned.includes(stage.id));
  if (inOrder.length === 0) return "";

  const ordinals = inOrder
    .map((stage) => TRIMESTER_ORDINAL[stage.id])
    .filter((ordinal): ordinal is string => ordinal !== null);
  const parts: string[] = [];
  if (ordinals.length > 0) {
    const [head, ...rest] = ordinals;
    parts.push(`${joinWords([head, ...rest.map(midSentence)])} trimester`);
  }
  // "New mum stage" rather than the bare label, so the phrase carries a noun
  // in every shape. The heading puts an article in front of it, and "you have
  // the new mum" is not a sentence about anything.
  const newMum = inOrder.find((stage) => stage.id === "new-mum");
  if (newMum) parts.push(`${newMum.label} stage`);

  if (parts.length === 1) return parts[0];
  return `${parts[0]} and ${midSentence(parts[1])}`;
}

/**
 * The shape of her plan, which is what picks the heading and the lede.
 *
 * `all` wins over the per-stage list, because the whole journey is one thing
 * she bought rather than four, and it can arrive with no per-stage rows at all.
 */
export type MumHubPlanShape = "whole" | "some" | "free";

export function planShape(entitlements: MumHubEntitlements): MumHubPlanShape {
  if (entitlements.all) return "whole";
  return ownedStageIdsInOrder(entitlements).length > 0 ? "some" : "free";
}

/**
 * The stages named in `stages`, in journey order, ignoring `all`.
 *
 * `all` is deliberately not folded in: it is a different sentence, and a
 * caller that wants to conflate them has to say so. A slug this build does not
 * know is dropped rather than guessed at.
 */
export function ownedStageIdsInOrder(
  entitlements: MumHubEntitlements,
): MumHubStageId[] {
  return MUM_HUB_STAGES.filter(
    (stage) => entitlements.stages[stage.slug] !== undefined,
  ).map((stage) => stage.id);
}

/**
 * When she first bought anything, as a British date, or null.
 *
 * The EARLIEST `purchasedAt` across her stages, because "member since" is
 * about the day she joined rather than the day she last spent money. Compared
 * as strings, which is exact rather than lucky: ISO 8601 sorts
 * lexicographically by design, and parsing four dates to compare them would
 * introduce a time zone where there is none.
 *
 * A row with a missing or unreadable date is skipped rather than treated as
 * the beginning of time, which is the difference between a quiet omission and
 * telling her she has been a member since 1970.
 */
export function memberSinceDate(entitlements: MumHubEntitlements): string | null {
  let earliest: string | null = null;
  for (const row of Object.values(entitlements.stages)) {
    const raw = row?.purchasedAt;
    if (!raw) continue;
    // Formatted first, so a string this module cannot read is dropped before
    // it can win the comparison.
    if (formatMembershipDate(raw) === null) continue;
    if (earliest === null || raw < earliest) earliest = raw;
  }
  return earliest === null ? null : formatMembershipDate(earliest);
}

/**
 * When the whole-journey grant ends, printed, or null (MAR-247).
 *
 * Only for somebody who actually has the journey. A stage owner's own end dates
 * are on her rows, and printing the journey's line for her would attach a date
 * to something she has not bought.
 */
export function journeyAccessUntilDate(
  entitlements: MumHubEntitlements,
): string | null {
  if (!entitlements.all || !entitlements.expiresAt) return null;
  return formatMembershipDate(entitlements.expiresAt);
}

/** Where a stage was bought, when it was bought rather than given. */
export type MumHubPurchasePlace = "website" | "app";

export interface MumHubStageRow {
  id: MumHubStageId;
  /** Content slug, which is how the payload keys her stages. */
  slug: string;
  label: string;
  included: boolean;
  /**
   * The key `/checkout` and the paywall know this stage by, and whether the
   * Worker's ladder currently offers it. A row she does not have but that is
   * on offer gets an "Unlock" that opens the paywall on this very stage
   * (Adrian, 2026-09-06); a row not on offer stays a plain "Not included".
   */
  productKey: MumHubProductKey;
  offered: boolean;
  /**
   * Where it came from, or null when there is nothing honest to say.
   *
   * Null covers two real cases: a stage she does not have, and the whole
   * journey, which is one purchase carrying one receipt rather than four
   * per-stage rows. Inventing a line for either would be a claim.
   */
  place: MumHubPurchasePlace | null;
  /**
   * True when it was granted rather than bought, so there is no purchase to
   * date. Covers both ways of granting: by hand, and by the allow-list.
   */
  granted: boolean;
  /** The purchase date, printed, or null when none arrived or it was junk. */
  purchasedOn: string | null;
  /**
   * When this stage's access ends, printed, or null (MAR-247).
   *
   * Null covers a stage she does not have, a grant with no end date, and a date
   * this module cannot read. All three draw nothing, because every sentence
   * available for them would be a claim about how long she has.
   */
  accessUntil: string | null;
  /**
   * A receipt she can open, or null.
   *
   * Present when the Worker sent one and it is an http(s) URL, and never
   * inferred from anything else on the row. It becomes an `href`, so a
   * `javascript:` string that got as far as this module must not become a
   * link.
   */
  receiptUrl: string | null;
}

/**
 * Where a purchase was made, for the small label.
 *
 * From `origin` and nothing else. Null for a grant, and null for an origin
 * this build has no words for, which `readEntitlements` hands over as
 * `unknown`: a shop nobody has taught this page about is better left unnamed
 * than called the wrong one.
 */
function placeFor(row: MumHubStageEntitlement): MumHubPurchasePlace | null {
  if (row.kind !== "purchase") return null;
  if (row.origin === "web") return "website";
  if (row.origin === "app_store") return "app";
  return null;
}

/** Whether a string is safe to put in an `href` a customer will click. */
export function isSafeReceiptUrl(value: unknown): value is string {
  if (typeof value !== "string" || value === "") return false;
  try {
    const url = new URL(value);
    return url.protocol === "https:" || url.protocol === "http:";
  } catch {
    return false;
  }
}

/**
 * The stages this panel may list, in journey order (MAR-133).
 *
 * Adrian, 2026-09-13, verbatim: "don't show the 1st trimester if the person is
 * in the second one, the same for the others. Don't show past trimesters as
 * they won't be able to use it."
 *
 * So a stage is listed when it is one she OWNS, whatever it is, or when it is
 * her stage today or one still ahead of her. A stage behind her that she does
 * not own is not listed at all: there is nothing she can do with it and nothing
 * we would sell her, and a permanent "Not included" against it is a reproach
 * rather than a fact worth printing.
 *
 * All four when she owns everything, and all four when her stage is not KNOWN.
 * `stageKnown: false` means she has given no usable due date and the Worker has
 * assumed the start of the journey; hiding three of four stages on an
 * assumption would be presenting a guess as a fact about her.
 */
export function visibleMembershipStages(
  entitlements: MumHubEntitlements,
): MumHubStageDescriptor[] {
  const every = [...MUM_HUB_STAGES];
  const today = entitlements.stageToday ?? null;
  if (entitlements.all || entitlements.stageKnown !== true || today === null) {
    return every;
  }
  const from = every.findIndex((stage) => stage.slug === today);
  // A slug this build cannot place is not a reason to hide anything. Showing
  // all four is what this panel did before the rule existed, and it is the safe
  // direction: it can waste a row, never hide one she owns.
  if (from < 0) return every;
  return every.filter(
    (stage, index) =>
      index >= from || entitlements.stages[stage.slug] !== undefined,
  );
}

/**
 * One row per stage she can still use, in journey order, plus the ones she
 * owns.
 *
 * Every such stage is listed even when she has none of them, because "Your
 * stages" is a picture of the journey rather than a receipt: a list that showed
 * only what she had bought would be empty for the customer most in need of
 * seeing what is on offer. Which stages those are is
 * {@link visibleMembershipStages}.
 */
export function membershipStageRows(
  entitlements: MumHubEntitlements,
): MumHubStageRow[] {
  return visibleMembershipStages(entitlements).map((stage) => {
    const row = entitlements.stages[stage.slug];
    const included = entitlements.all || row !== undefined;
    const place = row ? placeFor(row) : null;
    return {
      id: stage.id,
      slug: stage.slug,
      label: stage.label,
      included,
      productKey: stage.productKey,
      offered: !included && entitlements.offerKeys.includes(stage.productKey),
      place,
      // `kind`, not the absence of a place. A purchase from a shop this build
      // cannot name is still a purchase, and calling it a gift from Maria
      // would be the wrong half of the sentence to guess at.
      granted: row !== undefined && row.kind === "grant",
      purchasedOn:
        row && row.kind === "purchase"
          ? formatMembershipDate(row.purchasedAt)
          : null,
      // Off the Worker's own field and nothing else. The journey's end date is
      // a different grant and is drawn once, above the list.
      accessUntil: row?.expiresAt ? formatMembershipDate(row.expiresAt) : null,
      // A link because the Worker sent one, never because of what shop the row
      // names. The Worker fills this in only for a purchase made on the web,
      // and second-guessing it here would mean two places deciding when a
      // receipt exists, which is one more than can stay in step. The URL is
      // still checked, because it becomes an href a customer clicks.
      receiptUrl:
        row && isSafeReceiptUrl(row.receiptUrl) ? row.receiptUrl : null,
    };
  });
}

/**
 * The stage she is in today, from her due date, or null.
 *
 * Null covers no date, an unreadable date, and a date whose window has run
 * out. All three mean the page has no stage to name beside her date.
 */
export function currentStageId(
  dueDate: string | null,
  now: Date,
): MumHubStageId | null {
  if (!dueDate) return null;
  const result = deriveMumHubStage(dueDate, now);
  return result && result.kind === "stage" ? result.stage : null;
}

/** Her stage's label, for the due date row. */
export function currentStageLabel(
  dueDate: string | null,
  now: Date,
): string | null {
  const id = currentStageId(dueDate, now);
  return id === null ? null : stageDescriptor(id).label;
}

// ---------------------------------------------------------------------------
// Opening the paywall from somewhere else
// ---------------------------------------------------------------------------

/**
 * The marker that opens the hub's paywall on arrival (MAR-236).
 *
 * The paywall is a view inside `/mum-hub/stage`'s dialog stack and had no way
 * in from another page. Rather than lift it into a route of its own, which
 * would need its own copy of the stage summary and the offer list, the hub
 * honours one query parameter and opens the panel it already has.
 */
export const MUM_HUB_PAYWALL_PARAM = "paywall";

/** Where "Unlock" on the membership page sends her. */
export const MUM_HUB_PAYWALL_HREF = `/mum-hub/stage?${MUM_HUB_PAYWALL_PARAM}=1`;

/**
 * Where a single stage's "Unlock" sends her: the same paywall, opened with
 * that stage already chosen. The value is the product key, so the hub can
 * preselect it without a second vocabulary.
 */
export function paywallHrefFor(productKey: MumHubProductKey): string {
  return `/mum-hub/stage?${MUM_HUB_PAYWALL_PARAM}=${encodeURIComponent(productKey)}`;
}

/**
 * Whether a query string asks for the paywall. Exactly `1`, or a product key
 * this build sells; anything looser is not an instruction.
 */
export function shouldOpenPaywall(search: string): boolean {
  const value = new URLSearchParams(search).get(MUM_HUB_PAYWALL_PARAM);
  return value === "1" || (value !== null && isMumHubProductKey(value));
}

/**
 * The product the marker asked to preselect, or null for a bare `paywall=1`
 * and for any value that is not a product key.
 */
export function requestedPaywallProduct(search: string): MumHubProductKey | null {
  const value = new URLSearchParams(search).get(MUM_HUB_PAYWALL_PARAM);
  return value !== null && isMumHubProductKey(value) ? value : null;
}

/**
 * The same query string with the marker removed, so a refresh does not reopen
 * a panel she closed. Returns "" or a string starting with "?".
 */
export function withoutPaywallParam(search: string): string {
  const params = new URLSearchParams(search);
  params.delete(MUM_HUB_PAYWALL_PARAM);
  const rest = params.toString();
  return rest ? `?${rest}` : "";
}

// ---------------------------------------------------------------------------
// Opening membership over the account page
// ---------------------------------------------------------------------------

/**
 * The marker that opens the membership sheet over `/account` (MAR-238).
 *
 * Adrian, on a phone: "it should open a popup or bottom sheet on mobile view,
 * so it doesn't end up in endless pages that it's not a clear path out". Her
 * plan is now a panel over the account page rather than a page of its own, and
 * this parameter is what keeps that panel addressable.
 *
 * It earns its place by making the browser's back button do the obvious thing.
 * Opening the sheet pushes `?membership=1`, so back pops it and the sheet
 * closes over the account page she was already reading, rather than taking her
 * off the page entirely. It also survives a link, a refresh and a share:
 * `/account?membership=1` opens the sheet on the first paint.
 */
export const MUM_HUB_MEMBERSHIP_PARAM = "membership";

/** Where the old `/account/membership` page now sends her. */
export const MUM_HUB_MEMBERSHIP_HREF = `/account?${MUM_HUB_MEMBERSHIP_PARAM}=1`;

/**
 * Which of the sheet's two views a query string asks for (MAR-241).
 *
 * Adrian, arriving at the date form from the membership sheet: "instead of
 * opening a new popup, the content of the current one should change nicely".
 * So her date is now a second view INSIDE the membership sheet, and the marker
 * that made the sheet addressable grew a second value rather than gaining a
 * second parameter: `?membership=1` is her plan, `?membership=date` is her
 * date over it.
 *
 * One parameter with two values, because the two views are one panel and
 * exactly one of them is on top. Two independent flags could say "membership
 * closed, date open", which is a state this sheet does not have, and something
 * would then have to decide what to draw for it.
 *
 * Null for anything else, including `?membership=0` and a bare `?membership`.
 * A marker is an instruction, and a value this build does not know is not one.
 */
export type MumHubMembershipView = "membership" | "dueDate";

/** The value the date view puts on the marker. */
export const MUM_HUB_MEMBERSHIP_DATE_VALUE = "date";

export function membershipView(search: string): MumHubMembershipView | null {
  const value = new URLSearchParams(search).get(MUM_HUB_MEMBERSHIP_PARAM);
  if (value === "1") return "membership";
  if (value === MUM_HUB_MEMBERSHIP_DATE_VALUE) return "dueDate";
  return null;
}

/** Whether a query string asks for the sheet at all, on either view. */
export function shouldOpenMembership(search: string): boolean {
  return membershipView(search) !== null;
}

/**
 * The same query string with the marker added, keeping whatever else is on it.
 * Returns a string starting with "?".
 */
export function withMembershipParam(
  search: string,
  view: MumHubMembershipView = "membership",
): string {
  const params = new URLSearchParams(search);
  params.set(
    MUM_HUB_MEMBERSHIP_PARAM,
    view === "dueDate" ? MUM_HUB_MEMBERSHIP_DATE_VALUE : "1",
  );
  return `?${params.toString()}`;
}

/**
 * The same query string with the marker removed. Returns "" or a string
 * starting with "?", so a caller can append it to a pathname either way.
 */
export function withoutMembershipParam(search: string): string {
  const params = new URLSearchParams(search);
  params.delete(MUM_HUB_MEMBERSHIP_PARAM);
  const rest = params.toString();
  return rest ? `?${rest}` : "";
}

// ---------------------------------------------------------------------------
// Opening the date form over the hub
// ---------------------------------------------------------------------------

/**
 * The marker that opens the date sheet over `/mum-hub/stage` (MAR-239).
 *
 * Adrian, after the membership sheet: "open the change my date as a pop up or
 * bottom sheet too from mum hub". Changing a date already saved is a small
 * edit to something she is looking at, so it happens over her stage rather
 * than at a page she has to come back from.
 *
 * The third marker with the same shape as {@link MUM_HUB_PAYWALL_PARAM} and
 * {@link MUM_HUB_MEMBERSHIP_PARAM}, and for the same reason: opening pushes
 * it, so the browser's back button closes the sheet instead of leaving the
 * page, and a link can name a panel rather than only a page.
 *
 * It says only that the sheet is open. Her date is never in a URL.
 */
export const MUM_HUB_DATE_PARAM = "date";

/** Where "Change my date" points when she already has one. */
export const MUM_HUB_DATE_HREF = `/mum-hub/stage?${MUM_HUB_DATE_PARAM}=1`;

/** Whether a query string asks for the date sheet. Exactly `1`. */
export function shouldOpenDate(search: string): boolean {
  return new URLSearchParams(search).get(MUM_HUB_DATE_PARAM) === "1";
}

/** The same query string without the marker. "" or a string starting "?". */
export function withoutDateParam(search: string): string {
  const params = new URLSearchParams(search);
  params.delete(MUM_HUB_DATE_PARAM);
  const rest = params.toString();
  return rest ? `?${rest}` : "";
}
